'use client'

import styled, { ThemeProvider } from 'styled-components'

import { ArrowOutlink, Badge, b2bTheme } from '@syconium/little-miss-figgy'
import { TextLink } from '@syconium/little-miss-figgy/dist/components/TextLink'

import {
  type GetUserOrganizationsForPrimaryNavQuery,
  OrganizationMinimalFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import { useAuthentication } from '../../_providers/AuthenticationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { NextDialogProvider, NextDialogTrigger } from '../dialogs/NextDialog.client'
import { OrganizationSelectionDialog } from '../dialogs/OrganizationSelectionDialog.client'

import {
  PrimaryNavItem,
  PrimaryNavItemLink,
  PrimaryNavItemList,
  PrimaryNavItemListDivider,
  type PrimaryNavItemProps,
} from './PrimaryNavItemListSection.client'

export const PrimaryNavAccountMenuBody = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: ${o => o.theme.spacing(2)};
  justify-content: center;

  > * {
    white-space: nowrap;
  }
`

export const PrimaryNavAccountMenuInitialsIcon = styled.div<{ $highlightBorder?: boolean }>`
  color: ${o => o.theme.color.text.primary.on.subtle};
  align-items: center;
  background: ${o => o.theme.color.fill.subtle};
  border-radius: 50%;
  display: flex;
  font-size: 9px;
  font-weight: 600;
  height: 24px;
  justify-content: center;
  text-transform: uppercase;
  width: 24px;
  border: ${o =>
    o.$highlightBorder
      ? `2px solid ${o.theme.color.stroke.celebratory.text.on.background}`
      : 'none'};
`
export const CtaBadgeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap-reverse;
  width: 100%;
  a {
    font-weight: 700;
  }
  gap: ${o => o.theme.spacing(4)};
`

const PrimaryNavAdminDashboardContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${o => o.theme.spacing(1)};
`

type PrimaryNavAccountLogoutLinkProps = {
  className?: string
  children: React.ReactNode
}

export const PrimaryNavAccountLogoutLink = (props: PrimaryNavAccountLogoutLinkProps) => {
  const { logOut } = useAuthentication()
  return (
    <TextLink
      as='button'
      variant={'underline-on-hover'}
      inheritTypography
      onClick={() => {
        logOut()
      }}
      {...props}
    />
  )
}

const PrimaryNavAdminDashboardAction = ({
  adminOrganizations,
}: {
  adminOrganizations: OrganizationMinimalFieldsFragment[]
}) => {
  const {
    magnolia: {
      navigation: {
        organizations: { adminDashboard },
      },
    },
  } = useTranslation()
  const content = (
    <PrimaryNavAdminDashboardContent>
      <span>{adminDashboard}</span>
      <ArrowOutlink width={20} height={20} />
    </PrimaryNavAdminDashboardContent>
  )

  if (adminOrganizations.length > 1) {
    return (
      <NextDialogProvider>
        <NextDialogTrigger
          render={
            <TextLink as='button' inheritTypography variant='underline-on-hover' type='button'>
              {content}
            </TextLink>
          }
        />
        <OrganizationSelectionDialog />
      </NextDialogProvider>
    )
  }

  return (
    <PrimaryNavItemLink href={`/organizations/${adminOrganizations[0]?.id}`}>
      {content}
    </PrimaryNavItemLink>
  )
}

export const PrimaryNavOrganizationsMenuContent = ({
  data: { organizations, activeTeamStore },
  variant,
}: {
  data: GetUserOrganizationsForPrimaryNavQuery['my']
  variant?: PrimaryNavItemProps['$variant']
}) => {
  const {
    magnolia: {
      navigation: {
        organizations: { teamOwner, teamAdmin, teamsCTA },
      },
    },
  } = useTranslation()

  const adminOrganizations = organizations.filter(o =>
    ['OWNER', 'ADMIN'].includes(o.currentUserRole)
  )

  return (
    <ThemeProvider theme={b2bTheme}>
      <PrimaryNavItemListDivider />
      <PrimaryNavItemList>
        <PrimaryNavItem $variant={variant}>
          <CtaBadgeRow>
            <PrimaryNavItemLink href='/myfigs/teams'>{teamsCTA}</PrimaryNavItemLink>
            {adminOrganizations.length === 1 && adminOrganizations[0] && (
              <Badge
                variant='pill'
                size='small'
                colorVariant={
                  adminOrganizations[0].currentUserRole === 'OWNER' ? 'primary' : 'info'
                }
              >
                {adminOrganizations[0].currentUserRole === 'OWNER' ? teamOwner : teamAdmin}
              </Badge>
            )}
          </CtaBadgeRow>
        </PrimaryNavItem>
        {adminOrganizations.length > 0 && (
          <PrimaryNavItem $variant={variant}>
            <PrimaryNavAdminDashboardAction adminOrganizations={adminOrganizations} />
          </PrimaryNavItem>
        )}
        {activeTeamStore ? (
          <PrimaryNavItem $variant={variant}>
            <PrimaryNavItemLink href={`/team-stores/${activeTeamStore.id}`}>
              {activeTeamStore.name}
            </PrimaryNavItemLink>
          </PrimaryNavItem>
        ) : null}
      </PrimaryNavItemList>
    </ThemeProvider>
  )
}

export const PrimaryAccountLink = ({ route = '', label }: { route?: string; label: string }) => {
  return <PrimaryNavItemLink href={`/myfigs/${route}`}>{label}</PrimaryNavItemLink>
}

export const PrimaryNavLoginLink = () => {
  const translations = useTranslation()

  return (
    <PrimaryNavItemLink href={'/account/login'}>
      {translations.magnolia.account.logIn}
    </PrimaryNavItemLink>
  )
}

export const PrimaryNavRegisterLink = () => {
  const translations = useTranslation()

  return (
    <PrimaryNavItemLink href={'/account/register'}>
      {translations.magnolia.account.createAccount}
    </PrimaryNavItemLink>
  )
}

export const PrimaryNavFavoritesLink = () => {
  const translations = useTranslation()

  return (
    <PrimaryNavItemLink href={'/favorites'}>{translations.favorites.pageTitle}</PrimaryNavItemLink>
  )
}
